import { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { ABTest, ABTestVariant } from '@/hooks/useABTests';

interface DeclareWinnerModalProps {
  test: ABTest & { variants?: ABTestVariant[] };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (variantName: string) => Promise<void>;
}

export function DeclareWinnerModal({
  test,
  open,
  onOpenChange,
  onConfirm,
}: DeclareWinnerModalProps) {
  const [selectedVariant, setSelectedVariant] = useState<string>(
    test.variants?.[0]?.variantName || 'A'
  );
  const [isConfirming, setIsConfirming] = useState(false);

  const variants = test.variants || [];
  const totalScans = variants.reduce((sum, v) => sum + v.scanCount, 0);
  const leadingVariant = variants.reduce(
    (max, v) => (v.scanCount > (max?.scanCount || 0) ? v : max),
    variants[0]
  );

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm(selectedVariant);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to declare winner:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Declare Winner
          </DialogTitle>
          <DialogDescription>
            Choose the winning variant for "{test.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup value={selectedVariant} onValueChange={setSelectedVariant}>
            {variants.map((variant) => {
              const percentage =
                totalScans > 0 ? (variant.scanCount / totalScans) * 100 : 0;
              const isLeading = variant.id === leadingVariant?.id && totalScans > 0;

              return (
                <motion.div
                  key={variant.id}
                  whileHover={{ scale: 1.01 }}
                  className={cn(
                    'relative flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors',
                    selectedVariant === variant.variantName
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/30'
                  )}
                  onClick={() => setSelectedVariant(variant.variantName)}
                >
                  <RadioGroupItem
                    value={variant.variantName}
                    id={variant.id}
                    className="sr-only"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor={variant.id}
                        className="font-medium cursor-pointer flex items-center gap-2"
                      >
                        Variant {variant.variantName}
                        {isLeading && (
                          <Badge variant="secondary" className="text-xs">
                            Leading
                          </Badge>
                        )}
                      </Label>
                      <span className="text-sm font-mono">
                        {variant.scanCount} scans
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {percentage.toFixed(1)}% of total traffic
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </RadioGroup>

          <div className="flex items-start gap-3 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-yellow-700">This action is final</p>
              <p className="text-yellow-600">
                The winning variant's configuration will be applied to your main QR code.
                Other variants will redirect to the winner.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isConfirming}>
            {isConfirming ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Declaring...
              </>
            ) : (
              <>
                <Trophy className="h-4 w-4 mr-2" />
                Declare Variant {selectedVariant} Winner
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
